import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIdleTimer, workerTimers } from 'react-idle-timer';
import { paths } from '../../routing';
import LoadingSpinner from '../../components/loading-spinner';
import { ConsentScreen } from './components/ConsentScreen';
import { SigningViewerPdfJs as SigningViewer } from './components/SigningViewerPdfJs';
import { CompletionScreen } from './components/CompletionScreen';
import { WaitingScreen } from './components/WaitingScreen';
import { ErrorScreen } from './components/ErrorScreen';
import { SigningTimeoutModal } from './components/SigningTimeoutModal';
import { useSigningSession } from './hooks/useSigningSession';
import styles from './styles/NativeSigning.module.scss';

export type SigningStep = 'loading' | 'waiting' | 'consent' | 'signing' | 'complete' | 'error';

// Idle timeout constants - 3 minutes
const IDLE_TIMEOUT_MINUTES = 3;
const IDLE_TIMEOUT_MILLISECONDS = 1000 * 60 * IDLE_TIMEOUT_MINUTES;

const NativeSigningPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<SigningStep>('loading');
  const [error, setError] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);

  const { sessionData, loading, error: sessionError, startSession, submitSignature } = useSigningSession(token!);

  // Use the new useIdleTimer hook with v5 API
  const { reset } = useIdleTimer({
    timeout: IDLE_TIMEOUT_MILLISECONDS,
    timers: workerTimers, // Use worker timers to avoid browser throttling
    throttle: 500, // Throttle events to prevent spam
    eventsThrottle: 500, // Throttle event processing
    crossTab: false, // Disable crossTab to avoid bugs from v4
    events: [
      // Explicitly define events (exclude visibilitychange to avoid false resets)
      'mousemove',
      'keydown',
      'wheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'click',
      'scroll',
    ],
    onIdle: () => {
      // Only show timeout modal if actively signing
      if (step === 'signing') {
        setShowTimeoutModal(true);
      }
    },
  });

  useEffect(() => {
    if (!token) {
      navigate(paths.signError);
      return;
    }

    if (sessionError) {
      setError(sessionError);
      setStep('error');
      return;
    }

    // Only determine initial step once, when data first loads
    if (!loading && sessionData && !hasInitialized) {
      setHasInitialized(true);

      // Determine initial step based on session status
      if (sessionData.status === 'completed') {
        setStep('complete');
      } else if (sessionData.status === 'in_progress') {
        // Session has been started, show signing viewer
        setStep('signing');
      } else if (!sessionData.can_sign) {
        setStep('error');
        setError('This signing session is no longer valid');
      } else if (!sessionData.is_current_signer) {
        setStep('waiting');
      } else {
        setStep('consent');
      }
    }
  }, [loading, sessionData, sessionError, token, navigate, hasInitialized]);

  const handleConsentAccept = async () => {
    try {
      const result = await startSession();
      if (result.success) {
        setStep('signing');
      } else {
        setError(result.error || 'Failed to start signing session');
        setStep('error');
      }
    } catch (err) {
      setError('An error occurred while starting the signing session');
      setStep('error');
    }
  };

  const handleSigningComplete = async (
    signatureData: string,
    initialsData: string,
    fieldValues?: Record<
      string,
      {
        value: string;
        type: 'text' | 'date';
        coordinates: { x: number; y: number; width: number; height: number; page: number };
        signerId: string;
        signerEmail: string;
      }
    >
  ) => {
    try {
      // Updated for PDF.js - no XFDF data needed
      const result = await submitSignature(signatureData, initialsData, true, fieldValues);
      if (result.success) {
        setStep('complete');
      } else {
        setError(result.error || 'Failed to complete signing');
        setStep('error');
      }
    } catch (err) {
      setError('An error occurred while submitting your signature');
      setStep('error');
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel signing?')) {
      navigate(paths.home);
    }
  };

  // Idle timeout handlers
  const handleTimeoutModalClose = () => {
    setShowTimeoutModal(false);
    reset(); // Reset the idle timer using the new v5 API
  };

  const handleTimeoutExpired = () => {
    // Redirect to Propdocs homepage for security
    window.location.href = 'https://www.propdocs.com';
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={styles.nativeSigningContainer}>
      {step === 'waiting' && sessionData && (
        <WaitingScreen
          document={sessionData.document}
          currentSigner={sessionData.current_signer}
          onRefresh={() => window.location.reload()}
        />
      )}

      {step === 'consent' && sessionData && (
        <ConsentScreen
          document={sessionData.document}
          signer={sessionData.signer}
          sender={sessionData.sender}
          onAccept={handleConsentAccept}
        />
      )}

      {step === 'signing' && sessionData && (
        <SigningViewer session={sessionData} onComplete={handleSigningComplete} onCancel={handleCancel} />
      )}

      {step === 'complete' && sessionData && (
        <CompletionScreen
          document={sessionData.document}
          downloadUrl={`/api/v1/native-signing/${token}/download`}
          onClose={() => navigate(paths.home)}
        />
      )}

      {step === 'error' && (
        <ErrorScreen
          error={error || 'An unexpected error occurred'}
          onRetry={() => window.location.reload()}
          onClose={() => navigate(paths.home)}
        />
      )}

      {/* Timeout modal */}
      <SigningTimeoutModal show={showTimeoutModal} onClose={handleTimeoutModalClose} onTimeOut={handleTimeoutExpired} />
    </div>
  );
};

export default NativeSigningPage;
